'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

type LoginValues = { email: string; password: string; remember: boolean };
type RegisterValues = {
  accountType: 'individual' | 'corporate' | 'custody';
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  kycAck: boolean;
};
type MFAValues = { code: string };

export default function AuthForm() {
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string>('');
  const router = useRouter();
  const { signIn, signUp, getMFAFactors, challengeAndVerifyMFA } = useAuth();

  const loginForm = useForm<LoginValues>({ defaultValues: { email: '', password: '', remember: false } });
  const registerForm = useForm<RegisterValues>({ defaultValues: { accountType: 'individual', fullName: '', email: '', phone: '', password: '', confirmPassword: '', kycAck: false } });
  const mfaForm = useForm<MFAValues>({ defaultValues: { code: '' } });

  const getRoleRoute = (role: string): string => {
    if (role === 'staff') return '/staff-operations-portal';
    if (role === 'admin') return '/admin-control-panel';
    return '/customer-account-dashboard';
  };

  const onLogin = async (data: LoginValues) => {
    setLoading(true);
    try {
      const result = await signIn(data.email, data.password);

      // Read role from user_profiles (DB) instead of JWT user_metadata
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const userId = result?.user?.id;
      let role = 'customer';
      if (userId) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', userId)
          .single();
        if (profile?.role) role = profile.role;
      }

      // Check if MFA is required
      const factors = await getMFAFactors().catch(() => []);
      const verifiedFactors = factors.filter((f: any) => f.status === 'verified');

      if (verifiedFactors.length > 0) {
        // User has MFA enrolled — challenge them
        setMfaFactorId(verifiedFactors[0].id);
        setMfaStep(true);
        setLoading(false);
        return;
      }

      toast.success(`Welcome back! Signed in as ${role}`);
      router.push(getRoleRoute(role));
      router.refresh();
    } catch (err: any) {
      loginForm.setError('email', {
        message: err?.message || 'Invalid credentials. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const onMFAVerify = async (data: MFAValues) => {
    setLoading(true);
    try {
      await challengeAndVerifyMFA(mfaFactorId, data.code.trim());
      // Fetch role from user_profiles (DB) after MFA verification
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      let role = 'customer';
      if (user?.id) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        if (profile?.role) role = profile.role;
      }
      toast.success('MFA verified — access granted');
      router.push(getRoleRoute(role));
      router.refresh();
    } catch (err: any) {
      mfaForm.setError('code', {
        message: err?.message || 'Invalid verification code. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const onRegister = async (data: RegisterValues) => {
    if (data.password !== data.confirmPassword) {
      registerForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        role: 'customer',
        accountType: data.accountType,
        phone: data.phone,
      });
      // Clear any previous setup flow dismissal so it auto-triggers for new account
      if (typeof window !== 'undefined') {
        localStorage.removeItem('kp_setup_flow_dismissed');
        localStorage.removeItem('kp_setup_flow_step');
      }
      toast.success('Registration submitted — our compliance team will verify your identity within 2 business days.');
      setTab('login');
    } catch (err: any) {
      registerForm.setError('email', {
        message: err?.message || 'Registration failed. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  // ── MFA Step ──────────────────────────────────────────────────────────────
  if (mfaStep) {
    return (
      <div className="fade-in">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-accent/10 border border-accent/30 flex items-center justify-center flex-shrink-0">
            <Icon name="ShieldCheckIcon" size={20} className="text-accent" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-primary">Two-Factor Verification</h2>
            <p className="text-muted-foreground text-sm">Enter the 6-digit code from your authenticator app</p>
          </div>
        </div>

        <form onSubmit={mfaForm.handleSubmit(onMFAVerify)} className="space-y-5">
          <div>
            <label className="block text-[13px] font-600 text-foreground mb-1.5">Verification Code</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              autoFocus
              className="w-full bg-input border border-border rounded-lg px-3.5 py-3 text-[20px] font-mono text-center tracking-[0.4em] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
              {...mfaForm.register('code', {
                required: 'Code is required',
                minLength: { value: 6, message: 'Enter 6-digit code' },
                maxLength: { value: 6, message: 'Enter 6-digit code' },
              })}
            />
            {mfaForm.formState.errors.code && (
              <p className="text-danger text-[12px] mt-1.5">{mfaForm.formState.errors.code.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full vault-gradient text-white font-600 py-3 rounded-lg text-[14px] transition-all duration-150 active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2 shadow-card-md"
          >
            {loading ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="12" />
                </svg>
                Verifying…
              </>
            ) : (
              'Verify & Sign In'
            )}
          </button>

          <button
            type="button"
            onClick={() => { setMfaStep(false); setMfaFactorId(''); }}
            className="w-full text-[13px] text-muted-foreground hover:text-foreground transition-colors py-2"
          >
            ← Back to Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Tabs */}
      <div className="flex bg-muted rounded-xl p-1 mb-8">
        {(['login', 'register'] as const).map((t) => (
          <button
            key={`tab-${t}`}
            onClick={() => setTab(t)}
            className={`flex-1 py-2.5 rounded-lg text-[13.5px] font-600 transition-all duration-200 ${
              tab === t ? 'bg-card text-primary shadow-card' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        ))}
      </div>

      {tab === 'login' ? (
        <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-5">
          <div>
            <h2 className="text-2xl font-bold text-primary mb-1">Welcome back</h2>
            <p className="text-muted-foreground text-sm">Sign in to your secure account</p>
          </div>

          <div>
            <label className="block text-[13px] font-600 text-foreground mb-1.5">Email Address</label>
            <input
              type="email"
              autoComplete="email"
              placeholder="you@example.co.uk"
              className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
              {...loginForm.register('email', { required: 'Email is required' })}
            />
            {loginForm.formState.errors.email && (
              <p className="text-danger text-[12px] mt-1.5">{loginForm.formState.errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-[13px] font-600 text-foreground mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••••"
                className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 pr-11 text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                {...loginForm.register('password', { required: 'Password is required' })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
              </button>
            </div>
            {loginForm.formState.errors.password && (
              <p className="text-danger text-[12px] mt-1.5">{loginForm.formState.errors.password.message}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 rounded accent-primary" {...loginForm.register('remember')} />
              <span className="text-[13px] text-muted-foreground">Remember this device</span>
            </label>
            <button type="button" className="text-[13px] text-accent font-600 hover:underline">Forgot password?</button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full vault-gradient text-white font-600 py-3 rounded-lg text-[14px] transition-all duration-150 active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2 shadow-card-md"
          >
            {loading ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="12" />
                </svg>
                Verifying…
              </>
            ) : (
              'Sign In to Secure Account'
            )}
          </button>

          {/* MFA notice */}
          <div className="flex items-start gap-2.5 bg-navy-light border border-navy/10 rounded-lg p-3">
            <Icon name="ShieldCheckIcon" size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <p className="text-[12px] text-muted-foreground leading-relaxed">
              Multi-factor authentication is enforced for accounts with MFA enabled. High-value operations require additional verification.
            </p>
          </div>

          {/* Demo credentials — removed */}
        </form>
      ) : (
        <form onSubmit={registerForm.handleSubmit(onRegister)} className="space-y-5">
          <div>
            <h2 className="text-2xl font-bold text-primary mb-1">Open a Secure Account</h2>
            <p className="text-muted-foreground text-sm">Identity verification required before activation</p>
          </div>

          {/* Account type */}
          <div>
            <label className="block text-[13px] font-600 text-foreground mb-2">Account Type</label>
            <div className="grid grid-cols-3 gap-2">
              {([
                { value: 'individual', label: 'Individual', icon: 'UserIcon' },
                { value: 'corporate',  label: 'Corporate',  icon: 'BuildingOfficeIcon' },
                { value: 'custody',    label: 'Custody',    icon: 'ArchiveBoxIcon' },
              ] as const).map((opt) => (
                <label
                  key={`actype-${opt.value}`}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    registerForm.watch('accountType') === opt.value
                      ? 'border-accent bg-gold-light' :'border-border bg-input hover:border-primary/30'
                  }`}
                >
                  <input type="radio" value={opt.value} className="sr-only" {...registerForm.register('accountType')} />
                  <Icon name={opt.icon as never} size={20} className={registerForm.watch('accountType') === opt.value ? 'text-accent' : 'text-muted-foreground'} />
                  <span className={`text-[12px] font-600 ${registerForm.watch('accountType') === opt.value ? 'text-accent' : 'text-muted-foreground'}`}>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-600 text-foreground mb-1.5">Full Legal Name</label>
            <input type="text" placeholder="As on government-issued ID" className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-ring transition-all" {...registerForm.register('fullName', { required: 'Full name is required' })} />
            {registerForm.formState.errors.fullName && <p className="text-danger text-[12px] mt-1">{registerForm.formState.errors.fullName.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[13px] font-600 text-foreground mb-1.5">Email Address</label>
              <input type="email" placeholder="you@domain.com" className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-ring transition-all" {...registerForm.register('email', { required: 'Email required' })} />
              {registerForm.formState.errors.email && <p className="text-danger text-[12px] mt-1">{registerForm.formState.errors.email.message}</p>}
            </div>
            <div>
              <label className="block text-[13px] font-600 text-foreground mb-1.5">Phone Number</label>
              <input type="tel" placeholder="+44 7700 000000" className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-ring transition-all" {...registerForm.register('phone', { required: 'Phone required' })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[13px] font-600 text-foreground mb-1.5">Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} placeholder="Min 10 characters" className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 pr-10 text-[14px] focus:outline-none focus:ring-2 focus:ring-ring transition-all" {...registerForm.register('password', { required: 'Password required', minLength: { value: 10, message: 'Minimum 10 characters' } })} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                </button>
              </div>
              {registerForm.formState.errors.password && <p className="text-danger text-[12px] mt-1">{registerForm.formState.errors.password.message}</p>}
            </div>
            <div>
              <label className="block text-[13px] font-600 text-foreground mb-1.5">Confirm Password</label>
              <div className="relative">
                <input type={showConfirm ? 'text' : 'password'} placeholder="Repeat password" className="w-full bg-input border border-border rounded-lg px-3.5 py-2.5 pr-10 text-[14px] focus:outline-none focus:ring-2 focus:ring-ring transition-all" {...registerForm.register('confirmPassword', { required: 'Please confirm' })} />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                </button>
              </div>
              {registerForm.formState.errors.confirmPassword && <p className="text-danger text-[12px] mt-1">{registerForm.formState.errors.confirmPassword.message}</p>}
            </div>
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 rounded accent-primary mt-0.5 flex-shrink-0" {...registerForm.register('kycAck', { required: 'You must acknowledge KYC requirements' })} />
            <span className="text-[12px] text-muted-foreground leading-relaxed">
              I acknowledge that identity verification (KYC), source-of-funds declaration, and compliance checks are required before my account is activated. I agree to the{' '}
              <span className="text-accent font-600 cursor-pointer hover:underline">Terms of Service</span> and{' '}
              <span className="text-accent font-600 cursor-pointer hover:underline">Privacy Policy</span>.
            </span>
          </label>
          {registerForm.formState.errors.kycAck && <p className="text-danger text-[12px] -mt-3">{registerForm.formState.errors.kycAck.message}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full vault-gradient text-white font-600 py-3 rounded-lg text-[14px] transition-all duration-150 active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2 shadow-card-md"
          >
            {loading ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="12" />
                </svg>
                Submitting…
              </>
            ) : (
              'Submit Registration Request'
            )}
          </button>

          <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
            Account activation requires identity verification. Our compliance team reviews all applications within 2 business days.
          </p>
        </form>
      )}
    </div>
  );
}